import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import {
  useRenewalTemplates,
  useCreateRenewalTemplate,
  useUpdateRenewalTemplate,
  useDeleteRenewalTemplate,
  useReorderRenewalTemplates,
} from '@/hooks/use-api'
import { CalendarClock, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2 } from 'lucide-react'
import type { RenewalTaskTemplate } from '@/types'
import { explainError } from '@/lib/explain-error'

interface TemplateFormData {
  days_before: number | ''
  subject: string
  priority: string
  description: string
}

const EMPTY_FORM: TemplateFormData = {
  days_before: '',
  subject: '',
  priority: 'Normal',
  description: '',
}

export function RenewalTaskSettings() {
  const { toast } = useToast()
  const { data: templates, isLoading } = useRenewalTemplates()
  const createMutation = useCreateRenewalTemplate()
  const updateMutation = useUpdateRenewalTemplate()
  const deleteMutation = useDeleteRenewalTemplate()
  const reorderMutation = useReorderRenewalTemplates()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formData, setFormData] = useState<TemplateFormData>(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const openCreate = () => {
    setEditingId(null)
    setFormData(EMPTY_FORM)
    setFormErrors({})
    setDialogOpen(true)
  }

  const openEdit = (template: RenewalTaskTemplate) => {
    setEditingId(template.id)
    setFormData({
      days_before: template.days_before,
      subject: template.subject,
      priority: template.priority,
      description: template.description || '',
    })
    setFormErrors({})
    setDialogOpen(true)
  }

  const handleFieldChange = (field: keyof TemplateFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = (): boolean => {
    const errors: Record<string, string> = {}
    const days = Number(formData.days_before)
    if (!formData.days_before || !Number.isInteger(days) || days < 1 || days > 365) {
      errors.days_before = 'Must be between 1 and 365'
    }
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return

    try {
      const payload = {
        days_before: Number(formData.days_before),
        subject: formData.subject.trim(),
        priority: formData.priority,
        description: formData.description,
      }

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, updates: payload })
        toast({ title: 'Success', description: 'Template updated.' })
      } else {
        await createMutation.mutateAsync(payload)
        toast({ title: 'Success', description: 'Template created.' })
      }
      setDialogOpen(false)
    } catch (error) {
      const msg = explainError(error, 'Failed to save template')
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteMutation.mutateAsync(id)
      toast({ title: 'Success', description: 'Template removed.' })
    } catch {
      toast({ title: 'Error', description: 'Failed to remove template.', variant: 'destructive' })
    }
  }

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    if (!templates) return
    const items = [...templates]
    const swapIdx = direction === 'up' ? index - 1 : index + 1
    if (swapIdx < 0 || swapIdx >= items.length) return

    const order = items.map((t, i) => {
      if (i === index) return { id: t.id, sort_order: swapIdx }
      if (i === swapIdx) return { id: t.id, sort_order: index }
      return { id: t.id, sort_order: i }
    })

    try {
      await reorderMutation.mutateAsync(order)
    } catch {
      toast({ title: 'Error', description: 'Failed to reorder.', variant: 'destructive' })
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              <div>
                <CardTitle>Renewal Task Templates</CardTitle>
                <CardDescription>
                  Auto-create reminder tasks when an opportunity closes as Closed Won. Tasks are
                  scheduled relative to the contract end date.
                </CardDescription>
              </div>
            </div>
            <Button size="sm" onClick={openCreate} aria-label="Add renewal template">
              <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              Loading templates...
            </div>
          ) : !templates || templates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No renewal templates configured. Add one to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Days Before</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead className="w-[100px]">Priority</TableHead>
                  <TableHead className="w-[140px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template, idx) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">{template.days_before}</TableCell>
                    <TableCell>{template.subject}</TableCell>
                    <TableCell>{template.priority}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleMove(idx, 'up')}
                          disabled={idx === 0 || reorderMutation.isPending}
                          aria-label="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleMove(idx, 'down')}
                          disabled={idx === templates.length - 1 || reorderMutation.isPending}
                          aria-label="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEdit(template)}
                          aria-label={`Edit ${template.subject}`}
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(template.id)}
                          disabled={deleteMutation.isPending}
                          aria-label={`Delete ${template.subject}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="renewal-template-form">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Renewal Template' : 'Add Renewal Template'}
            </DialogTitle>
          </DialogHeader>
          <div id="renewal-template-form" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="days_before">Days Before Renewal *</Label>
              <Input
                id="days_before"
                type="number"
                min={1}
                max={365}
                value={formData.days_before}
                onChange={(e) =>
                  handleFieldChange(
                    'days_before',
                    e.target.value ? parseInt(e.target.value, 10) : ''
                  )
                }
                placeholder="e.g. 90"
                aria-required="true"
                aria-invalid={!!formErrors.days_before}
                aria-describedby={formErrors.days_before ? 'days-error' : undefined}
              />
              {formErrors.days_before && (
                <p id="days-error" className="text-sm text-destructive" role="alert">
                  {formErrors.days_before}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-subject">Subject *</Label>
              <Input
                id="template-subject"
                value={formData.subject}
                onChange={(e) => handleFieldChange('subject', e.target.value)}
                placeholder="e.g. Renewal Follow-Up: 90 Days"
                aria-required="true"
                aria-invalid={!!formErrors.subject}
                aria-describedby={formErrors.subject ? 'subject-error' : undefined}
              />
              {formErrors.subject && (
                <p id="subject-error" className="text-sm text-destructive" role="alert">
                  {formErrors.subject}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(v) => handleFieldChange('priority', v)}
              >
                <SelectTrigger id="template-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-description">Description</Label>
              <Textarea
                id="template-description"
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                rows={3}
                placeholder="Optional description for the generated task"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
